import React, { useEffect, useState } from 'react';

import useScroll from 'hooks/useScroll';
import { useTranslation } from 'react-i18next';
import MappingIcon from 'assets/icons/mapping.svg';
import ManualEntryIcon from 'assets/icons/manualentry.svg';
import ExcelIcon from 'assets/icons/excel.svg';
import CsvIcon from 'assets/icons/csv.svg';
import { DealItem } from '../models/partner';
import DealCards from './dealCards';
import PlatformCards, { PlatformCardItem } from './platformCards';
import AnchorTabsHeader, { AnchorTabsHeaderGroup } from './anchorTabsHeader';

export interface AnchorGroup<T> {
  label: string,
  items: T[],
}

export enum AnchorTabType {
  IMPORT = 'import',
  PARTNER = 'partner',
}

export interface AnchorTabsProps {
  groups: AnchorGroup<unknown>[];
  type: AnchorTabType;
  showHeader: boolean;
  hidesEmptySections: boolean;
  rightElement?: React.ReactElement;
}

function AnchorTabs(props: AnchorTabsProps) {
  const { scrollY } = useScroll();
  const [activeIndex, setActiveIndex] = useState(0);
  const groupRefs = React.useRef<HTMLDivElement[]>([]);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const scrollTabIndexUpdate = (sections: HTMLDivElement[], tabs: HTMLDivElement) => {
    const offset = tabs.getBoundingClientRect().height;

    const isSectionActive = (bbox: DOMRect, _offset: number) => (
      bbox.top - _offset < 0 && Math.abs(bbox.top - _offset) <= bbox.height
    );

    const index = sections.findIndex((section: HTMLDivElement) => {
      const bbox = section.getBoundingClientRect();

      return isSectionActive(bbox, offset);
    });

    if (sections.length === 0) {
      return;
    }

    const firstSectionBbox = sections[0].getBoundingClientRect();
    const lastSectionBbox = sections[sections.length - 1].getBoundingClientRect();

    let currentIndex;
    if (index <= 0 && firstSectionBbox.top >= 0) {
      currentIndex = 0;
    } else if (index <= 0 && lastSectionBbox.top <= 0) {
      currentIndex = sections.length - 1;
    } else {
      currentIndex = index;
    }

    setActiveIndex(currentIndex);
  };

  useEffect(() => {
    const currentGroupRefs = groupRefs.current;
    const currentTabsRef = tabsRef.current;

    if (currentGroupRefs && currentTabsRef) {
      scrollTabIndexUpdate(currentGroupRefs, currentTabsRef);
    }
  }, [scrollY]);

  const handleTabNavigation = (index: number) => {
    setActiveIndex(index);

    const currentGroup = groupRefs.current[index];
    const currentTabsRef = tabsRef.current;
    const offset = currentTabsRef ? currentTabsRef.getBoundingClientRect().height - 1 : 0;
    const top = currentGroup.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: 'smooth' });
  };

  const addGroupRef = (ref: HTMLDivElement) => {
    if (ref !== null && !groupRefs.current.includes(ref)) {
      groupRefs.current.push(ref);
    }
  };

  const getHeaderGroup = (item: AnchorGroup<unknown>[])
  : AnchorTabsHeaderGroup[] => item.map((group: AnchorGroup<unknown>, index: number) => {
    const itemMap = {
      label: group.label,
      isActive: group.items.length > 0,
    } as AnchorTabsHeaderGroup;

    return itemMap;
  });

  const customGroupItems = [{
    icon: <MappingIcon />,
    name: `${t('Custom', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Custom', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/import/custom/',
    custom_csv_uri: null,
    beta: false,
  }, {
    icon: <ExcelIcon />,
    name: `${t('Excel', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Excel', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/import/import_xls/',
    custom_csv_uri: null,
    beta: false,
  }, {
    icon: <CsvIcon />,
    name: `${t('CSV', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('CSV', { ns: 'imports' })}`,
    custom_api_uri: null,
    custom_csv_uri: 'https://cointracking.info/import/import_csv/',
    beta: false,
  }, {
    icon: <ManualEntryIcon />,
    name: `${t('Manual Entry', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Manual Entry', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/enter_coins.php',
    custom_csv_uri: null,
    beta: false,
  }];

  return (
    <div className="ct_anchorTabs">
      <div ref={tabsRef} className="ct_anchorTabs-tabsWrapper">

      <AnchorTabsHeader
        groups={getHeaderGroup(props.groups)}
        showHeader={props.showHeader}
        handleTabNavigation={(index: number) => {
          handleTabNavigation(index);
        }}
        modifiers={[]}
        rightElement={props.rightElement}
      />

      </div>

      <div className="ct_anchorTabs-sections">
        {props.groups.some((group: AnchorGroup<unknown>) => group.items.length) ? (
          <>
            {props.groups.map((group: AnchorGroup<unknown>, index: number) => (
              <div key={group.label} id={group.label} ref={addGroupRef} className={`ct_anchorTabs-section ${!group.items.length ? '_disabled' : ''}`}>
                {(!props.hidesEmptySections || (props.hidesEmptySections && group.items.length)) ? (
                  <div className={`ct_anchorTabs-title _${props.type}`}>
                    {t(group.label, { ns: 'imports' })}
                  </div>
                ) : (<p />)}
                {!!group.items.length && (
                  <div className="ct_anchorTabs-content">
                    {props.type && props.type === AnchorTabType.IMPORT && (
                      <PlatformCards items={group.items as PlatformCardItem[]} />
                    )}
                    {props.type && props.type === AnchorTabType.PARTNER && (
                      <DealCards items={group.items as DealItem[]} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            <div className={`ct_anchorTabs-title _${props.type}`}>{t('No import found, please use one of the following options:', { ns: 'imports' })}</div>
            <PlatformCards items={customGroupItems as PlatformCardItem[]} />
          </>
        )}
      </div>
    </div>
  );
}

AnchorTabs.defaultProps = { ...{} };

export default AnchorTabs;
